import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'utils/i18n';
import { MarketplaceCard } from './MarketplaceCard';
import { ProductInfoModal } from './ProductInfoModal';
import { filterMarketplaceProducts } from '../ResourceDetailModal/utils/marketplaceMetadata';
import marketplaceTranslations from './translations';

const PRODUCTS_PER_PAGE = 12;

/**
 * Marketplace product grid component with pagination
 * Displays filtered and sorted products in a grid layout
 */
export const MarketplaceGrid = ({
  products,
  filters,
  selectedProductId,
  onProductSelect,
  loading = false,
  isAuthenticated = true,
  loadingProducts = false,
  openModalProductId = null,
  setOpenModalProductId = null,
  onOpenProvider = null,
  viewModeToggle = null,
  isPublicPortal = false
}) => {
  const { language } = useTranslation();
  const t = (key, defaultValue = '') => {
    const keys = key.split('.');
    let value = marketplaceTranslations[language] || marketplaceTranslations.en;
    for (const k of keys) {
      value = value?.[k];
    }
    return value || defaultValue || key;
  };
  const [currentPage, setCurrentPage] = useState(1);
  const [localOpenModalProductId, setLocalOpenModalProductId] = useState(null);
  const isControlled = setOpenModalProductId != null;
  const effectiveOpenId = isControlled ? (openModalProductId ?? null) : localOpenModalProductId;
  const setEffectiveOpenId = isControlled ? (id) => setOpenModalProductId(id ?? null) : setLocalOpenModalProductId;

  // Filter products based on current filters
  // Note: Removed console.log calls from useMemo to avoid React warnings
  // (Logger component intercepts console.log and updates state during render)
  const filteredProducts = React.useMemo(() => {
    const result = filterMarketplaceProducts(products, filters);
    return result;
  }, [products, filters]);
  
  // Reset to page 1 when filters change (moved from useMemo to avoid side effects during render)
  useEffect(() => {
    setCurrentPage(1);
  }, [products, filters]);
  
  // Calculate pagination
  const totalPages = Math.ceil(filteredProducts.length / PRODUCTS_PER_PAGE);
  const startIndex = (currentPage - 1) * PRODUCTS_PER_PAGE;
  const endIndex = startIndex + PRODUCTS_PER_PAGE;
  const paginatedProducts = filteredProducts.slice(startIndex, endIndex);
  
  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
    // Scroll to top of grid
    const gridElement = document.querySelector('.marketplace-grid');
    if (gridElement) {
      gridElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };
  
  const handlePrevious = () => {
    if (currentPage > 1) {
      handlePageChange(currentPage - 1);
    }
  };
  
  const handleNext = () => {
    if (currentPage < totalPages) {
      handlePageChange(currentPage + 1);
    }
  };
  
  if (loading || loadingProducts) {
    return (
      <div className="marketplace-grid-section">
        <div className="marketplace-results-header">
          <div className="marketplace-results-count">
            <span className="marketplace-skeleton-count" aria-hidden="true" />
          </div>
        </div>
        <div className="marketplace-grid loading" aria-busy="true" aria-label={t('pagination.loading', 'Loading products')}>
          {[1, 2, 3, 4, 5, 6].map(i => (
            <div key={i} className="marketplace-card marketplace-card-skeleton palantir-style left-aligned" role="presentation">
              <div className="marketplace-card-top-band marketplace-skeleton-top">
                <div className="product-icon-top-left">
                  <div className="marketplace-skeleton-icon" />
                  <div className="marketplace-skeleton-type" />
                </div>
                <div className="product-provider-logo-top-right">
                  <div className="marketplace-skeleton-badge" />
                </div>
              </div>
              <div className="product-card-content-wrapper">
                <div className="product-card-top-content">
                  <div className="marketplace-skeleton-title" />
                  <div className="marketplace-skeleton-meta" />
                  <div className="marketplace-skeleton-desc">
                    <span /><span /><span />
                  </div>
                </div>
                <div className="product-card-bottom-row marketplace-skeleton-bottom">
                  <div className="marketplace-skeleton-tags" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }
  
  // Only show "No products found" if we're not loading and there are actually no products
  if (!loading && !loadingProducts && filteredProducts.length === 0 && products.length === 0) {
    return (
      <div className="marketplace-grid-section">
        <div className="marketplace-results-header">
          <div className="marketplace-results-count">
            <span>{t('empty.noProducts', 'No products found')}</span>
          </div>
        </div>
        <div className="marketplace-grid empty">
          <div className="empty-state">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
            </svg>
            <h3>{t('empty.noProducts', 'No products found')}</h3>
            <p>
              {filters.searchQuery 
                ? t('empty.noProductsMatchQuery', 'No products match "{query}". Try adjusting your search or filters.').replace('{query}', filters.searchQuery)
                : t('empty.noProductsMatchFilters', 'No products match the selected filters. Try adjusting your filters.')}
            </p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="marketplace-grid-section">
      {/* Results Header */}
      <div className="marketplace-results-header">
        <div className="marketplace-results-count">
          <span className="results-count-text">
            {t('pagination.showing', 'Showing')} {startIndex + 1}-{Math.min(endIndex, filteredProducts.length)} {t('pagination.of', 'of')} {filteredProducts.length} {t('pagination.products', 'products')}
          </span>
        </div>
        {totalPages > 1 && (
          <div className="marketplace-pagination-info">
            {t('pagination.page', 'Page')} {currentPage} {t('pagination.of', 'of')} {totalPages}
          </div>
        )}
        {viewModeToggle && <div style={{ marginLeft: 'auto' }}>{viewModeToggle}</div>}
      </div>
      
      {/* Product Grid - single modal owned by grid */}
      <div className="marketplace-grid">
        {paginatedProducts.map(product => {
          const isResaleProduct = product.marketplace?.vendorType === 'third_party' || !!product.metadata?.from_resale_resource;
          const disabled = !isAuthenticated || (isPublicPortal && isResaleProduct);
          return (
            <MarketplaceCard
              key={product.id}
              product={product}
              isSelected={selectedProductId === product.id}
              onSelect={!disabled && isAuthenticated ? onProductSelect : () => {}}
              loading={loading}
              disabled={disabled}
              onOpenModal={(p) => setEffectiveOpenId(p?.id ?? null)}
            />
          );
        })}
      </div>
      {effectiveOpenId && (() => {
        const product = paginatedProducts.find(p => p.id === effectiveOpenId) || products.find(p => p.id === effectiveOpenId);
        if (!product) return null;
        return createPortal(
          <ProductInfoModal
            product={product}
            onClose={() => setEffectiveOpenId(null)}
            onSelect={(p) => {
              if (isAuthenticated && onProductSelect) onProductSelect(p);
              setEffectiveOpenId(null);
            }}
            onOpenProvider={onOpenProvider}
          />,
          document.body
        );
      })()}
      
      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="marketplace-pagination">
          <button
            className="pagination-button pagination-prev"
            onClick={handlePrevious}
            disabled={currentPage === 1}
            aria-label={t('pagination.previousPage', 'Previous page')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M15 18l-6-6 6-6"/>
            </svg>
            {t('buttons.previous', 'Previous')}
          </button>
          
          <div className="pagination-pages">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
              // Show first page, last page, current page, and pages around current
              if (
                page === 1 ||
                page === totalPages ||
                (page >= currentPage - 1 && page <= currentPage + 1)
              ) {
                return (
                  <button
                    key={page}
                    className={`pagination-page ${currentPage === page ? 'active' : ''}`}
                    onClick={() => handlePageChange(page)}
                    aria-label={t('pagination.goToPage', 'Go to page {page}').replace('{page}', page)}
                  >
                    {page}
                  </button>
                );
              } else if (page === currentPage - 2 || page === currentPage + 2) {
                return <span key={page} className="pagination-ellipsis">...</span>;
              }
              return null;
            })}
          </div>
          
          <button
            className="pagination-button pagination-next"
            onClick={handleNext}
            disabled={currentPage === totalPages}
            aria-label={t('pagination.nextPage', 'Next page')}
          >
            {t('buttons.next', 'Next')}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 18l6-6-6-6"/>
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};


